import React, { useRef, useEffect } from 'react';
// Note: native fullscreen for this panel is handled by CSS (width:100vw +
// left:50%) so the inline translateX(-50%) still centers correctly.
const initialTranslateX = '-50%';

const CATEGORIES = [
{ key: 'food_drink',     icon: '🍽️', label: 'Food & Drink' },
  { key: 'nightlife',      icon: '🌙', label: 'Nightlife' },
  { key: 'outdoors',       icon: '🌿', label: 'Outdoors' },
  { key: 'active',         icon: '⚡', label: 'Active' },
  { key: 'shopping',       icon: '🛍️', label: 'Shopping' },
  { key: 'entertainment',  icon: '🎭', label: 'Entertainment' },
];

export default function FiltersPanel({
  searchRadius,
  setSearchRadius,
  rangeValues,
  getSliderIndex,
  getKmFromIndex,
  formatDistance,
  selectedCategories,
  setSelectedCategories,
  loadExistingEvents,
  updateRangeCircle,
}) {
  const panelRef = useRef(null);
  const handleRef = useRef(null);
  const drag = useRef({ active: false, startY: 0, startTranslateY: 0, panelH: 0 });

  // Read the current translateY value in px using computed transform matrix
  const getTranslateYPx = (el) => {
    try { return new DOMMatrix(getComputedStyle(el).transform).m42; } catch (e) { return 0; }
  };

  // How many px of the panel to keep visible in "half" state
  // (handle + title + search range + divider + categories header/subtitle)
  const HALF_VISIBLE_PX = 265;

  // Mirror the sheet's open/closed state onto <body> so CSS can react
  // (e.g. shrink the map avatar marker while filters are open).
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    const sync = () => {
      const open = panel.dataset.snap && panel.dataset.snap !== 'closed';
      document.body.classList.toggle('filters-open', !!open);
    };
    sync();
    const mo = new MutationObserver(sync);
    mo.observe(panel, { attributes: true, attributeFilter: ['data-snap'] });
    return () => { mo.disconnect(); document.body.classList.remove('filters-open'); };
  }, []);

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;

    // Snappier curve than ease-out (matches the native panel transitions)
    const SNAP_CURVE = 'cubic-bezier(0.32, 0.72, 0, 1)';
    let rafId = 0;
    let pendingY = null;
    // Per-gesture scratch (separate from drag.current so we can track scroll intent)
    const g = { active: false, decided: false, dragging: false, startScroll: 0 };

    const flush = () => {
      rafId = 0;
      if (pendingY == null) return;
      panel.style.transform = `translateX(${initialTranslateX}) translateY(${pendingY}px)`;
      pendingY = null;
    };

    const onTouchStart = (e) => {
      if (window.innerWidth > 800) return;
      // Let the range slider and buttons handle their own touches.
      if (e.target.closest('input') || e.target.closest('button')) {
        g.active = false;
        return;
      }
      g.active = true;
      g.decided = false;
      g.dragging = false;
      g.startScroll = panel.scrollTop;
      drag.current = {
        startY: e.touches[0].clientY,
        startTranslateY: getTranslateYPx(panel),
        panelH: panel.offsetHeight,
      };
      panel.style.transition = 'none';
    };

    const onTouchMove = (e) => {
      if (!g.active) return;
      const delta = e.touches[0].clientY - drag.current.startY;

      // Decide once whether this gesture drags the sheet or scrolls content.
      if (!g.decided) {
        if (Math.abs(delta) < 6) return; // wait for clear intent
        const snap = panel.dataset.snap || 'half';
        if (snap !== 'full') {
          // Not fully expanded → dragging always moves the sheet.
          g.dragging = true;
        } else {
          // Fully expanded → only drag the sheet when pulling DOWN from the top
          // of the scroll. Otherwise let the content scroll natively.
          g.dragging = g.startScroll <= 0 && delta > 0;
        }
        g.decided = true;
        if (g.dragging) {
          panel.style.willChange = 'transform';
        }
      }

      if (!g.dragging) return; // native scroll proceeds
      e.preventDefault();
      pendingY = Math.max(0, drag.current.startTranslateY + delta);
      if (!rafId) rafId = requestAnimationFrame(flush);
    };

    const onTouchEnd = () => {
      if (!g.active) return;
      g.active = false;
      if (!g.dragging) return; // was a scroll, nothing to snap
      if (rafId) { cancelAnimationFrame(rafId); rafId = 0; }
      pendingY = null;

      const { panelH } = drag.current;
      const currentY = getTranslateYPx(panel);
      const snapHalfPx = Math.max(0, panelH - HALF_VISIBLE_PX);

      panel.style.transition = `transform 0.32s ${SNAP_CURVE}`;
      setTimeout(() => { panel.style.willChange = ''; }, 360);

      if (currentY < snapHalfPx / 2) {
        panel.dataset.snap = 'full';
        panel.style.transform = `translateX(${initialTranslateX}) translateY(0px)`;
      } else if (currentY < (snapHalfPx + panelH) / 2) {
        panel.dataset.snap = 'half';
        panel.style.transform = `translateX(${initialTranslateX}) translateY(${snapHalfPx}px)`;
      } else {
        panel.dataset.snap = 'closed';
        panel.style.transform = `translateX(${initialTranslateX}) translateY(100%)`;
        setTimeout(() => { panel.style.bottom = '0'; }, 340);
        if (window.rangeCircle) { window.rangeCircle.remove(); window.rangeCircle = null; }
      }
    };

    // Whole container owns the gesture; scroll-awareness above keeps inner
    // scrolling intact.
    panel.addEventListener('touchstart', onTouchStart, { passive: true });
    panel.addEventListener('touchmove', onTouchMove, { passive: false });
    panel.addEventListener('touchend', onTouchEnd);
    panel.addEventListener('touchcancel', onTouchEnd);

    return () => {
      panel.removeEventListener('touchstart', onTouchStart);
      panel.removeEventListener('touchmove', onTouchMove);
      panel.removeEventListener('touchend', onTouchEnd);
      panel.removeEventListener('touchcancel', onTouchEnd);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  const handleRangeInput = (e) => {
    const index = parseInt(e.target.value);
    const newRadius = getKmFromIndex(index);
    setSearchRadius(newRadius);
    updateRangeCircle(newRadius);
  };

  const handleRangeChange = (e) => {
    const index = parseInt(e.target.value);
    const newRadius = getKmFromIndex(index);
    localStorage.setItem('searchRadius', newRadius.toString());
    if (window.markerReloadTimeout) clearTimeout(window.markerReloadTimeout);
    window.markerReloadTimeout = setTimeout(() => loadExistingEvents(newRadius), 150);
  };

  const handleClearCategories = () => {
    setSelectedCategories([]);
    localStorage.setItem('selectedCategories', JSON.stringify([]));
    window.selectedCategories = [];
    if (window.markerReloadTimeout) clearTimeout(window.markerReloadTimeout);
    window.markerReloadTimeout = setTimeout(() => loadExistingEvents(searchRadius), 150);
  };

  const handleToggleCategory = (key) => {
    const isActive = selectedCategories.includes(key);
    const newCategories = isActive
      ? selectedCategories.filter((c) => c !== key)
      : [...selectedCategories, key];
    setSelectedCategories(newCategories);
    localStorage.setItem('selectedCategories', JSON.stringify(newCategories));
    window.selectedCategories = newCategories;
    if (window.markerReloadTimeout) clearTimeout(window.markerReloadTimeout);
    window.markerReloadTimeout = setTimeout(() => loadExistingEvents(searchRadius), 300);
  };

  const sliderIndex = getSliderIndex(searchRadius);
  const sliderPercent = (sliderIndex / (rangeValues.length - 1)) * 100;

  return (
    <div
      ref={panelRef}
      id="rangeSliderPanel"
      style={{
position: 'fixed', bottom: '0', left: '50%',
transform: `translateX(${initialTranslateX}) translateY(100%)`,
width: 'calc(100% - 20px)', maxWidth: '400px', background: 'white',
        borderRadius: '16px 16px 0 0',
        boxShadow: '0 -8px 32px rgba(0,0,0,0.2), 0 -4px 16px rgba(0,0,0,0.15), 0 -2px 8px rgba(0,0,0,0.1)',
        padding: '20px 24px 24px', transition: 'transform 0.3s ease-out',
        zIndex: 1005, border: '2px solid #000000', borderBottom: 'none',
        maxHeight: '85vh', overflowY: 'auto',
      }}
    >
      {/* Drag handle */}
      <div
        ref={handleRef}
        style={{
          display: 'flex', justifyContent: 'center',
          marginBottom: '12px', padding: '4px 0 8px',
          cursor: 'grab', touchAction: 'none', userSelect: 'none',
        }}
      >
        <div style={{ width: '36px', height: '4px', background: '#d1d5db', borderRadius: '2px', pointerEvents: 'none' }}></div>
      </div>

      <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600', color: '#202124', textAlign: 'center' }}>
        Filters
      </h3>

      {/* Search Range */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="#5f6368" strokeWidth="2" fill="none" />
            <circle cx="12" cy="12" r="6"  stroke="#5f6368" strokeWidth="2" fill="none" />
            <circle cx="12" cy="12" r="2"  fill="#5f6368" />
          </svg>
          <span style={{ fontSize: '14px', fontWeight: '600', color: '#202124' }}>Search Range</span>
          <span style={{ fontSize: '14px', fontWeight: '600', color: '#2c2c2c', marginLeft: 'auto' }}>
            +{formatDistance(searchRadius)}
          </span>
        </div>
        <p style={{ margin: '0 0 10px 0', fontSize: '11px', color: '#5f6368', lineHeight: '1.4' }}>
          Shows others' events within this range.{' '}
          <span style={{ color: '#34a853', fontWeight: '500' }}>Your events are always visible.</span>
        </p>
        <input
          type="range"
          min="0"
          max={rangeValues.length - 1}
          value={sliderIndex}
          onInput={handleRangeInput}
          onChange={handleRangeChange}
          style={{
            width: '100%', height: '6px', borderRadius: '3px', outline: 'none',
            background: `linear-gradient(to right, #2c2c2c 0%, #2c2c2c ${sliderPercent}%, #e0e0e0 ${sliderPercent}%, #e0e0e0 100%)`,
            WebkitAppearance: 'none', appearance: 'none', cursor: 'pointer',
            transition: 'background 0.15s ease-out',
          }}
        />
      </div>

      {/* Divider */}
      <div style={{ height: '1px', background: '#e5e7eb', margin: '0 0 16px 0' }}></div>

      {/* Categories */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <span style={{ fontSize: '16px' }}>🏷️</span>
          <span style={{ fontSize: '14px', fontWeight: '600', color: '#202124' }}>Categories</span>
          {selectedCategories.length > 0 && (
            <button
              onClick={handleClearCategories}
              style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#ea4335', fontSize: '12px', fontWeight: '500', cursor: 'pointer', padding: '2px 6px' }}
            >
              Clear all
            </button>
          )}
        </div>
        <p style={{ margin: '0 0 10px 0', fontSize: '11px', color: '#5f6368', lineHeight: '1.4' }}>
          {selectedCategories.length === 0
            ? 'Showing all categories. Tap to filter.'
            : `Showing ${selectedCategories.length} selected.`}
        </p>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {CATEGORIES.map((cat) => {
            const isActive = selectedCategories.includes(cat.key);
            return (
              <button
                key={cat.key}
                onClick={() => handleToggleCategory(cat.key)}
                style={{
                  padding: '6px 10px',
                  border: `2px solid ${isActive ? '#2c2c2c' : '#e0e0e0'}`,
                  borderRadius: '20px',
                  background: isActive ? '#2c2c2c' : 'white',
                  color: isActive ? 'white' : '#202124',
                  fontSize: '12px',
                  fontWeight: isActive ? '600' : '400',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  whiteSpace: 'nowrap',
                }}
              >
                <span>{cat.icon}</span>
                <span>{cat.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <style>{`
        #rangeSliderPanel input[type="range"] { transition: background 0.15s ease-out; }
        #rangeSliderPanel input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none; appearance: none;
          width: 22px; height: 22px; border-radius: 50%;
          background: radial-gradient(circle, white 0%, white 35%, #2c2c2c 35%, #2c2c2c 100%);
          cursor: pointer; box-shadow: 0 2px 6px rgba(0,0,0,0.2);
          transition: transform 0.15s ease-out, box-shadow 0.15s ease-out;
        }
        #rangeSliderPanel input[type="range"]::-moz-range-thumb {
          width: 22px; height: 22px; border-radius: 50%;
          background: radial-gradient(circle, white 0%, white 35%, #2c2c2c 35%, #2c2c2c 100%);
          cursor: pointer; border: none; box-shadow: 0 2px 6px rgba(0,0,0,0.2);
          transition: transform 0.15s ease-out, box-shadow 0.15s ease-out;
        }
        #rangeSliderPanel input[type="range"]:active::-webkit-slider-thumb { transform: scale(1.15); box-shadow: 0 3px 8px rgba(0,0,0,0.3); }
        #rangeSliderPanel input[type="range"]:active::-moz-range-thumb    { transform: scale(1.15); box-shadow: 0 3px 8px rgba(0,0,0,0.3); }
        #rangeSliderPanel::-webkit-scrollbar { width: 4px; }
        #rangeSliderPanel::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 2px; }
        @media (max-width: 800px) {
  #rangeSliderPanel {
    margin-bottom: -20px;
  }
}
      `}</style>
    </div>
  );
}
